/**
 * Custom Wishlist System
 * Manages wishlist functionality using localStorage
 */

class CustomWishlist {
  constructor() {
    this.storageKey = 'custom_wishlist';
    this.items = this.getItems();
    this.init();
  }

  init() {
    // Update all wishlist buttons on page load
    this.updateAllButtons();
    this.updateHeaderCount();
    
    // Add event listeners
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.custom-wishlist-btn');
      if (btn) {
        e.preventDefault();
        this.toggleItem(btn);
      }
    });

    // Listen for storage changes (multi-tab sync)
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.items = this.getItems();
        this.updateAllButtons();
        this.updateHeaderCount();
      }
    });
  }

  getItems() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Wishlist: Error reading localStorage', e);
      return [];
    }
  }

  saveItems() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      
      // Trigger custom event for other components
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: this.items, count: this.items.length }
      }));
    } catch (e) {
      console.error('Wishlist: Error saving to localStorage', e);
    }
  }

  isInWishlist(productId) {
    return this.items.some(item => item.id === productId);
  }

  addItem(productId, productHandle, productTitle, productImage, productPrice, productUrl) {
    if (this.isInWishlist(productId)) return false;

    this.items.push({
      id: productId,
      handle: productHandle,
      title: productTitle,
      image: productImage,
      price: productPrice,
      url: productUrl,
      addedAt: new Date().toISOString()
    });

    this.saveItems();
    this.showNotification(`${productTitle} favorilere eklendi`, 'success');
    return true;
  }

  removeItem(productId) {
    const item = this.items.find(i => i.id === productId);
    this.items = this.items.filter(item => item.id !== productId);
    this.saveItems();
    
    if (item) {
      this.showNotification(`${item.title} favorilerden çıkarıldı`, 'info');
    }
    return true;
  }

  toggleItem(button) {
    const productId = parseInt(button.dataset.productId);
    const productHandle = button.dataset.productHandle;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;
    const productPrice = button.dataset.productPrice;
    const productUrl = button.dataset.productUrl;

    if (!productId) {
      console.error('Wishlist: Missing product ID');
      return;
    }

    const isInWishlist = this.isInWishlist(productId);

    // Animate button
    button.classList.add('wishlist-animating');
    setTimeout(() => button.classList.remove('wishlist-animating'), 300);

    if (isInWishlist) {
      this.removeItem(productId);
    } else {
      this.addItem(productId, productHandle, productTitle, productImage, productPrice, productUrl);
    }

    this.updateButton(button);
    this.updateHeaderCount();
  }

  updateButton(button) {
    const productId = parseInt(button.dataset.productId);
    const isInWishlist = this.isInWishlist(productId);
    
    if (isInWishlist) {
      button.classList.add('is-active');
      button.setAttribute('aria-label', 'Favorilerden çıkar');
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-label', 'Favorilere ekle');
    }
  }

  updateAllButtons() {
    document.querySelectorAll('.custom-wishlist-btn').forEach(btn => {
      this.updateButton(btn);
    });
  }

  updateHeaderCount() {
    const count = this.items.length;
    const counters = document.querySelectorAll('.custom-wishlist-count');
    
    counters.forEach(counter => {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  showNotification(message, type = 'info') {
    // Create toast notification
    const toast = document.createElement('div');
    toast.className = `custom-wishlist-toast custom-wishlist-toast--${type}`;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // Trigger animation
    setTimeout(() => toast.classList.add('is-visible'), 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  getCount() {
    return this.items.length;
  }

  getAllItems() {
    return this.items;
  }

  clear() {
    this.items = [];
    this.saveItems();
    this.updateAllButtons();
    this.updateHeaderCount();
  }
}

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.customWishlist = new CustomWishlist();
  });
} else {
  window.customWishlist = new CustomWishlist();
}

